import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import { SectionHeader } from '../../components/staff/ui/SectionHeader';
import { StaffLoadError, type StaffLoadErrorKind } from '../../components/staff/StaffLoadError';
import { apiFetch } from '../../utils/api';

type Fam = 'cave' | 'boissons' | 'cuisine';
interface FamilyCost { ca: number; achats: number; ratio: number | null }
interface FoodCost {
  periode: string;
  byFamily: Record<Fam, FamilyCost>;
  caTotalFood: number;
  achatsFood: number;
  ratioGlobal: number | null;
  triage: { triagees: number; faux: number; fauxHt: number; aRevoir: number };
}

interface TriageInvoice {
  id: string;
  date?: string;
  htPennylane: number;
  supplierMetadata: string | null;
  familleFinale: string;
  source: string;
  needsReview: boolean;
  isReal: boolean;
  triage: { docType?: string; supplierName?: string; reason?: string } | null;
  fileUrl?: string | null;
}
interface Nouvelle {
  id: string;
  date?: string;
  ht: number;
  supplier: string | null;
  fileUrl?: string | null;
}
interface TriageStatus {
  periode: string;
  totalPeriode: number;
  triagees: number;
  restantes: number;
  fausses: number;
  totalFaussesHt: number;
  aRevoir: number;
  nouvelles: Nouvelle[];
  invoices: TriageInvoice[];
}

// Mapping comptable Pennylane (cf. ZPennylaneMapping backend). Les valeurs
// categoryAccounts sont des ids de comptes Pennylane ; reservedAccounts est clé
// par NUMÉRO de compte (ex. « 707300 » Vin à emporter, réservé sans catégorie).
interface PennylaneMapping {
  journalId: string | number;
  categoryAccounts: Record<string, string | number>;
  vatAccounts: Record<string, string | number>;
  paymentAccounts: Record<string, string | number>;
  reservedAccounts?: Record<string, string | number>;
  labelPrefix?: string;
}

const FAM_LABEL: Record<Fam, string> = { cuisine: 'Cuisine', cave: 'Cave', boissons: 'Boissons' };
const eur0 = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const FAMILIES: { key: string; label: string }[] = [
  { key: 'cuisine', label: 'Cuisine' },
  { key: 'cave', label: 'Cave' },
  { key: 'boissons', label: 'Boissons' },
  { key: 'exclu', label: 'Hors food' },
];

function monthNow(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function StaffCompta() {
  const navigate = useNavigate();
  const month = monthNow();
  const [food, setFood] = useState<FoodCost | null>(null);
  const [triage, setTriage] = useState<TriageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<StaffLoadErrorKind | null>(null);
  const [mapping, setMapping] = useState<PennylaneMapping | null>(null);
  const [mapMsg, setMapMsg] = useState<string | null>(null);
  const [jsonDraft, setJsonDraft] = useState<string | null>(null); // null = éditeur fermé

  const loadMapping = useCallback(async () => {
    try {
      const r = await apiFetch('/pos/pennylane/mapping', { cache: 'no-store' });
      const m = r.ok ? await r.json() : null;
      setMapping(m && !m.error ? m : null);
    } catch { /* section optionnelle : on n'échoue pas l'écran pour ça */ }
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // no-store : on veut un état frais depuis le backend (réconciliation Pennylane),
      // jamais une réponse mise en cache par le webview/navigateur.
      const opts: RequestInit = { cache: 'no-store' };
      const [fr, tr] = await Promise.all([
        apiFetch(`/pos/food-cost?month=${month}&window=3`, opts),
        apiFetch(`/pos/invoices/triage?month=${month}&window=6`, opts),
      ]);
      if (fr.status === 401 || tr.status === 401) { setError('auth'); return; }
      const f = fr.ok ? await fr.json() : null;
      const t = tr.ok ? await tr.json() : null;
      if (f && !f.error) setFood(f);
      if (t && !t.error) setTriage(t);
      // Erreur seulement si les deux appels échouent (sinon on garde ce qu'on a).
      setError(!fr.ok && !tr.ok ? 'error' : null);
      await loadMapping();
    } catch { setError('error'); } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [month, loadMapping]);

  // Reconstruit le mapping 706x : résout/rattache les comptes du plan Pennylane
  // (706100/706200/706300 + 707300 réservé) et persiste. Idempotent.
  const rebuildMapping = async () => {
    setBusy('mapping'); setMapMsg(null);
    try {
      const r = await apiFetch('/pos/pennylane/setup-accounts', { method: 'POST' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setMapMsg(`Échec : ${body.error ?? r.status}`); return; }
      const created = body.created?.length ?? 0;
      const warns = body.warnings?.length ?? 0;
      setMapMsg(`Mapping reconstruit · ${created} compte(s) créé(s) · ${warns} avertissement(s)${warns ? ` : ${body.warnings.join(' ; ')}` : ''}`);
      await loadMapping();
    } catch { setMapMsg('Erreur réseau'); } finally { setBusy(null); }
  };

  // Édition avancée : PUT du mapping brut (validé Zod côté backend).
  const saveMappingJson = async () => {
    if (jsonDraft == null) return;
    setBusy('mapping'); setMapMsg(null);
    let parsed: unknown;
    try { parsed = JSON.parse(jsonDraft); } catch { setMapMsg('JSON invalide'); setBusy(null); return; }
    try {
      const r = await apiFetch('/pos/pennylane/mapping', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setMapMsg(`Refusé : ${JSON.stringify(body.detail ?? body.error ?? r.status)}`); return; }
      setMapMsg('Mapping enregistré');
      setJsonDraft(null);
      await loadMapping();
    } catch { setMapMsg('Erreur réseau'); } finally { setBusy(null); }
  };

  useEffect(() => { load(); }, [load]);

  // Décision manuelle d'une facture → famille / vrai-faux. Pour une NOUVELLE
  // (non triée), on sème date/HT/fournisseur pour que le backend crée la ligne.
  const classify = async (
    id: string,
    body: { family?: string; isReal?: boolean; date?: string; ht?: number; supplier?: string | null },
  ) => {
    setBusy(id);
    try {
      await apiFetch(`/pos/invoices/${id}/classification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await load();
    } catch { /* ignore */ } finally {
      setBusy(null);
    }
  };

  // Pré-analyse IA des factures non encore triées (optionnel, ~centimes).
  const triageIA = async () => {
    setBusy('triage');
    try {
      await apiFetch(`/pos/invoices/triage?month=${month}&window=6&limit=25`, { method: 'POST' });
      await load();
    } catch { /* ignore */ } finally {
      setBusy(null);
    }
  };

  // Lien « Voir » → ouvre le PDF Pennylane dans un onglet.
  const Voir = ({ url }: { url?: string | null }) =>
    url ? (
      <a className="link" href={url} target="_blank" rel="noreferrer">📄 Voir</a>
    ) : (
      <span className="staff-inbox-noview" title="PDF indisponible">PDF indispo</span>
    );

  const nouvelles = triage?.nouvelles ?? [];
  const faux = (triage?.invoices ?? []).filter((i) => i.triage && !i.isReal);
  const aRevoir = (triage?.invoices ?? []).filter((i) => i.needsReview && i.isReal);

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Compta</h1>
        <button
          className="staff-badge-v2"
          disabled={loading || refreshing || busy !== null}
          onClick={() => load()}
          title="Resynchroniser avec Pennylane"
        >
          {loading || refreshing ? '⟳ Synchro…' : '↻ Rafraîchir'}
        </button>
      </div>

      {loading && <p className="staff-empty">Chargement…</p>}
      {!loading && error && <StaffLoadError kind={error} />}

      {/* ── Food cost ── */}
      {food && (
        <div className="staff-section-v2">
          <SectionHeader title="Food cost" caption={`Ratio matières · ${food.periode}`} />
          <div className="staff-foodcost-grid">
            {(['cuisine', 'cave', 'boissons'] as Fam[]).map((k) => {
              const f = food.byFamily[k];
              const manquant = f.ca > 0 && f.achats === 0;
              return (
                <div key={k} className={`staff-fc-card ${manquant ? 'warn' : ''}`}>
                  <span className="staff-fc-fam">{FAM_LABEL[k]}</span>
                  <strong className="staff-fc-ratio">{f.ratio != null ? `${f.ratio.toFixed(0)} %` : '—'}</strong>
                  <span className="staff-fc-detail">{eur0(f.achats)} € / {eur0(f.ca)} €</span>
                  {manquant && <span className="staff-fc-alert">⚠️ achats manquants</span>}
                </div>
              );
            })}
          </div>
          <div className="staff-fc-global">
            Global : <strong>{food.ratioGlobal != null ? `${food.ratioGlobal.toFixed(0)} %` : '—'}</strong>
            <em> · {eur0(food.achatsFood)} € d'achats / {eur0(food.caTotalFood)} € de CA</em>
          </div>
        </div>
      )}

      {/* ── Mapping comptable Pennylane (catégories → comptes 706x) ── */}
      <div className="staff-section-v2">
        <SectionHeader
          title="Mapping comptable Pennylane"
          caption="Catégories carte → comptes de vente (706x)"
        />
        {mapping ? (
          <>
            <div className="staff-map-list">
              {Object.entries(mapping.categoryAccounts)
                .filter(([k]) => !k.includes('::')) // clés lisibles (libellé), pas les doublons id::taux
                .map(([cat, acc]) => (
                  <div key={cat} className="staff-map-row">
                    <span className="staff-map-cat">{cat}</span>
                    <span className="staff-map-acc">{String(acc)}</span>
                  </div>
                ))}
              {Object.entries(mapping.reservedAccounts ?? {}).map(([num, acc]) => (
                <div key={num} className="staff-map-row staff-map-reserved">
                  <span className="staff-map-cat">Réservé · {num}</span>
                  <span className="staff-map-acc">{String(acc)}</span>
                </div>
              ))}
            </div>
            {Object.keys(mapping.categoryAccounts).length === 0 && (
              <p className="staff-empty">Aucune catégorie rattachée — reconstruire le mapping.</p>
            )}
          </>
        ) : (
          <p className="staff-empty">Mapping non configuré. « Reconstruire » pour le générer depuis Pennylane.</p>
        )}

        <div className="staff-map-actions">
          <button disabled={busy === 'mapping'} onClick={rebuildMapping}>
            {busy === 'mapping' ? '⟳ En cours…' : '↻ Reconstruire le mapping 706x'}
          </button>
          <button
            className="link"
            onClick={() => setJsonDraft(jsonDraft == null ? JSON.stringify(mapping ?? {}, null, 2) : null)}
          >
            {jsonDraft == null ? 'Édition avancée (JSON)' : 'Fermer l’éditeur'}
          </button>
        </div>

        {jsonDraft != null && (
          <div className="staff-map-editor">
            <textarea
              className="staff-map-textarea"
              value={jsonDraft}
              spellCheck={false}
              onChange={(e) => setJsonDraft(e.target.value)}
              rows={12}
            />
            <button disabled={busy === 'mapping'} onClick={saveMappingJson}>Enregistrer le mapping</button>
          </div>
        )}

        {mapMsg && <p className="staff-map-msg">{mapMsg}</p>}
      </div>

      {/* ── Boîte de réception factures ── */}
      <div className="staff-section-v2">
        <SectionHeader
          title="Factures à traiter"
          caption={triage ? `${triage.totalPeriode} factures · ${triage.restantes} non analysées` : undefined}
        />

        {/* Nouvelles factures : consultables + affectables directement */}
        {nouvelles.length > 0 && (
          <>
            <div className="staff-inbox-subtitle">
              🆕 Nouvelles à valider ({nouvelles.length})
              <button className="link" disabled={busy === 'triage'} onClick={triageIA} style={{ marginLeft: 8 }}>
                {busy === 'triage' ? 'Analyse…' : 'Pré-analyser à l’IA'}
              </button>
            </div>
            {nouvelles.map((inv) => (
              <div key={inv.id} className="staff-inbox-card">
                <div className="staff-inbox-head">
                  <span>{inv.supplier || 'Fournisseur ?'}{inv.date ? ` · ${inv.date}` : ''}</span>
                  <strong>{eur0(inv.ht)} €</strong>
                </div>
                <div className="staff-inbox-actions">
                  <Voir url={inv.fileUrl} />
                  {FAMILIES.map((fam) => (
                    <button
                      key={fam.key}
                      disabled={busy === inv.id}
                      onClick={() => classify(inv.id, { family: fam.key, isReal: true, date: inv.date, ht: inv.ht, supplier: inv.supplier ?? undefined })}
                    >
                      {fam.label}
                    </button>
                  ))}
                  <button
                    className="danger"
                    disabled={busy === inv.id}
                    onClick={() => classify(inv.id, { isReal: false, date: inv.date, ht: inv.ht, supplier: inv.supplier ?? undefined })}
                  >
                    Pub / faux
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* À revoir : décision manuelle */}
        {aRevoir.length > 0 && (
          <>
            <div className="staff-inbox-subtitle">🔎 À trancher ({aRevoir.length})</div>
            {aRevoir.map((inv) => (
              <div key={inv.id} className="staff-inbox-card">
                <div className="staff-inbox-head">
                  <span>{inv.supplierMetadata || inv.triage?.supplierName || 'Fournisseur ?'}</span>
                  <strong>{eur0(inv.htPennylane)} €</strong>
                </div>
                {inv.triage?.reason && <p className="staff-inbox-reason">{inv.triage.reason}</p>}
                <div className="staff-inbox-actions">
                  <Voir url={inv.fileUrl} />
                  {FAMILIES.map((fam) => (
                    <button key={fam.key} disabled={busy === inv.id} onClick={() => classify(inv.id, { family: fam.key, isReal: true })}>
                      {fam.label}
                    </button>
                  ))}
                  <button className="danger" disabled={busy === inv.id} onClick={() => classify(inv.id, { isReal: false })}>
                    Pub / faux
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Fausses factures détectées */}
        {faux.length > 0 && (
          <>
            <div className="staff-inbox-subtitle">❌ Fausses détectées — à archiver dans Pennylane ({faux.length} · {eur0(triage?.totalFaussesHt ?? 0)} €)</div>
            {faux.map((inv) => (
              <div key={inv.id} className="staff-inbox-line">
                <span className="staff-inbox-type">{(inv.triage?.docType ?? '').toUpperCase()}</span>
                <span className="staff-inbox-sup">{inv.triage?.supplierName || inv.supplierMetadata || '?'}</span>
                <span className="staff-inbox-ht">{eur0(inv.htPennylane)} €</span>
                <Voir url={inv.fileUrl} />
                <button className="link" disabled={busy === inv.id} onClick={() => classify(inv.id, { isReal: true })} title="Ce n'est pas une pub">
                  c'est une vraie
                </button>
              </div>
            ))}
          </>
        )}

        {triage && nouvelles.length === 0 && aRevoir.length === 0 && faux.length === 0 && (
          <p className="staff-empty">Aucune facture à traiter 🎉</p>
        )}
      </div>
    </div>
  );
}
